/**
 * The unit record's vocabulary: every value OfficeHub keeps about a generator that has NO AppSheet
 * column (battery, fuel/tank, fire pump, controller, component extras, data-plate photos).
 *
 * ONE registry, read by the server writer (functions/src/forms/unitEnrichment), the FSR review
 * projection, and FSR prefill, so the three can never disagree about a key, a unit or a type.
 *
 * KEYS (the contract Init #2 and FIREPUMP replay through):
 *   unit:component:<type>:<field>    unit:battery:<bank>:<field>    unit:fuel:<field>
 * STORED AT: _opgUnit.components.<type>.<field> / _opgUnit.battery.<bank>.<field> / _opgUnit.fuel.<field>
 *
 * UNITS: every numeric field states its unit here and in its name; a value never carries a unit
 * as text ("500", never "500 GPM").
 *
 * APPSHEET COLUMNS are listed per component only so the writer can REFUSE them by name: those
 * columns have one writer, forms_approveComponentDeltas (charter §2, one writer per column).
 */

/** Components that can carry unit-record data. */
export const UNIT_COMPONENT_TYPES = [
  "generator",
  "engine",
  "alternator",
  "ats",
  "ats2",
  "firePump",
  "controller",
  "other",
] as const;
export type UnitComponentType = (typeof UNIT_COMPONENT_TYPES)[number];

/** What kind of value a field holds. `date` = ISO "YYYY", "YYYY-MM" or "YYYY-MM-DD". */
export type UnitValueType = "string" | "number" | "boolean" | "date" | "storagePath";

/**
 * How two values for the same field relate.
 *   nameplate   fixed by the manufacturer: a new value REPLACES (the old one goes to history).
 *   latestWins  true when observed (battery, a best photo): the value with the newest
 *               observedAt is current; an older observation is kept in history only.
 */
export type UnitFieldPolicy = "nameplate" | "latestWins";

export interface UnitFieldDef {
  readonly label: string;
  readonly valueType: UnitValueType;
  readonly policy: UnitFieldPolicy;
  /** Unit of a numeric value, e.g. "US gal/min". Absent for non-numeric fields. */
  readonly unit?: string;
  /** false = free text with no single plate value to confirm it against: plate proof must never offer it. Absent = provable by its type. */
  readonly provable?: false;
}

const str = (label: string): UnitFieldDef => ({ label, valueType: "string", policy: "nameplate" });
const num = (label: string, unit: string): UnitFieldDef => ({ label, valueType: "number", policy: "nameplate", unit });
const date = (label: string): UnitFieldDef => ({ label, valueType: "date", policy: "nameplate" });
const PHOTO: UnitFieldDef = { label: "Data plate photo", valueType: "storagePath", policy: "latestWins" };
/** Plate text that has no field of its own (a date, an option code). One per component. */
const NOTES: UnitFieldDef = { label: "Notes from the plate", valueType: "string", policy: "latestWins", provable: false };

export interface UnitComponentDef {
  readonly label: string;
  /** Fields the unit record owns for this component. */
  readonly fields: Readonly<Record<string, UnitFieldDef>>;
  /** Fields that live in an AppSheet Generators column: refused by the unit-record writer. */
  readonly appSheetFields: readonly string[];
}

/** make / model / serial as unit-record fields, for components with no AppSheet column. */
const IDENTITY = { make: str("Make"), model: str("Model"), serial: str("Serial number") } as const;
const APPSHEET_IDENTITY = ["make", "model", "serial"] as const;

export const UNIT_COMPONENTS: Readonly<Record<UnitComponentType, UnitComponentDef>> = {
  generator: {
    label: "Generator",
    fields: { photo: PHOTO, notes: NOTES },
    appSheetFields: [...APPSHEET_IDENTITY, "kw", "voltage", "spec", "fuel", "amps", "type"],
  },
  engine: {
    label: "Engine",
    fields: { ratedHp: num("Rated horsepower", "hp"), ratedRpm: num("Rated speed", "rpm"), photo: PHOTO, notes: NOTES },
    appSheetFields: APPSHEET_IDENTITY,
  },
  alternator: { label: "AC alternator / gen end", fields: { photo: PHOTO, notes: NOTES }, appSheetFields: APPSHEET_IDENTITY },
  ats: { label: "ATS", fields: { ampsRating: num("Amps rating", "A"), photo: PHOTO, notes: NOTES }, appSheetFields: APPSHEET_IDENTITY },
  ats2: { label: "ATS 2", fields: { ampsRating: num("Amps rating", "A"), photo: PHOTO, notes: NOTES }, appSheetFields: APPSHEET_IDENTITY },
  firePump: {
    label: "Fire pump",
    fields: {
      ...IDENTITY,
      ratedGpm: num("Rated flow", "US gal/min"),
      ratedPsi: num("Rated pressure", "psi"),
      ratedRpm: num("Rated speed", "rpm"),
      pumpSize: str("Pump size"),
      photo: PHOTO, notes: NOTES,
    },
    appSheetFields: [],
  },
  controller: {
    label: "Controller",
    fields: {
      ...IDENTITY,
      voltage: num("Voltage", "V"),
      maxPsi: num("Max system pressure", "psi"),
      mfgDate: date("Manufacture date"),
      photo: PHOTO, notes: NOTES,
    },
    appSheetFields: [],
  },
  other: { label: "Other component", fields: { label: str("What it is"), ...IDENTITY, photo: PHOTO, notes: NOTES }, appSheetFields: [] },
};

/** Battery banks. Historical forms carry one bank; later banks are reserved. */
export const UNIT_BATTERY_BANKS = ["main", "bank2", "bank3"] as const;
export type UnitBatteryBank = (typeof UNIT_BATTERY_BANKS)[number];

const reading = (label: string, valueType: UnitValueType, unit?: string): UnitFieldDef =>
  unit ? { label, valueType, policy: "latestWins", unit } : { label, valueType, policy: "latestWins" };

export const UNIT_BATTERY_FIELDS: Readonly<Record<string, UnitFieldDef>> = {
  quantity: reading("Quantity", "number", "batteries"),
  groupSize: reading("Group size (BCI)", "string"),
  systemVoltage: reading("System voltage", "number", "V"),
  chemistry: reading("Chemistry", "string"),
  modelNumber: reading("Model number", "string"),
  lastReplaced: reading("Last replaced", "date"),
};

export const UNIT_FUEL_FIELDS: Readonly<Record<string, UnitFieldDef>> = {
  fuelTankYn: { label: "Fuel tank present", valueType: "boolean", policy: "nameplate" },
  fuelTankCapacity: num("Fuel tank capacity", "US gal"),
  dayTankYn: { label: "Day tank present", valueType: "boolean", policy: "nameplate" },
  dayTankCapacity: num("Day tank capacity", "US gal"),
};

export type UnitRecordKey =
  | `unit:component:${UnitComponentType}:${string}`
  | `unit:battery:${UnitBatteryBank}:${string}`
  | `unit:fuel:${string}`;

/** A key resolved against the registry. `path` is the Firestore field path under the doc. */
export interface ResolvedUnitKey {
  readonly key: UnitRecordKey;
  readonly path: readonly [string, ...string[]];
  readonly def: UnitFieldDef;
}

export type ResolveUnitKeyResult =
  | { ok: true; resolved: ResolvedUnitKey }
  | { ok: false; reason: "unknown-key" | "appsheet-column-key" };

const has = <T extends string>(list: readonly T[], v: string): v is T => (list as readonly string[]).includes(v);
const own = (o: object, k: string): boolean => Object.prototype.hasOwnProperty.call(o, k);

/**
 * Resolve a unit-record key. Every segment is checked against a fixed list, so a key can never name
 * a path outside the three sections (no `__proto__`, no dots, no pin/geo/gallery).
 */
export function resolveUnitRecordKey(key: string): ResolveUnitKeyResult {
  const parts = typeof key === "string" ? key.split(":") : [];
  const unknown = { ok: false as const, reason: "unknown-key" as const };
  if (parts[0] !== "unit") return unknown;

  if (parts[1] === "component" && parts.length === 4) {
    const [, , type, field] = parts;
    if (!has(UNIT_COMPONENT_TYPES, type)) return unknown;
    const comp = UNIT_COMPONENTS[type];
    if (own(comp.fields, field)) {
      return { ok: true, resolved: { key: key as UnitRecordKey, path: ["_opgUnit", "components", type, field], def: comp.fields[field] } };
    }
    return has(comp.appSheetFields, field) ? { ok: false, reason: "appsheet-column-key" } : unknown;
  }
  if (parts[1] === "battery" && parts.length === 4) {
    const [, , bank, field] = parts;
    if (!has(UNIT_BATTERY_BANKS, bank) || !own(UNIT_BATTERY_FIELDS, field)) return unknown;
    return { ok: true, resolved: { key: key as UnitRecordKey, path: ["_opgUnit", "battery", bank, field], def: UNIT_BATTERY_FIELDS[field] } };
  }
  if (parts[1] === "fuel" && parts.length === 3) {
    const field = parts[2];
    if (!own(UNIT_FUEL_FIELDS, field)) return unknown;
    return { ok: true, resolved: { key: key as UnitRecordKey, path: ["_opgUnit", "fuel", field], def: UNIT_FUEL_FIELDS[field] } };
  }
  return unknown;
}

/** Matches "YYYY", "YYYY-MM" or "YYYY-MM-DD" with a real month/day. */
export function isIsoPartialDate(value: unknown): value is string {
  if (typeof value !== "string") return false;
  const m = /^(\d{4})(?:-(\d{2})(?:-(\d{2}))?)?$/.exec(value);
  if (!m) return false;
  const [, y, mo, d] = m;
  if (mo === undefined) return true;
  const month = Number(mo);
  if (month < 1 || month > 12) return false;
  if (d === undefined) return true;
  const day = Number(d);
  const last = new Date(Date.UTC(Number(y), month, 0)).getUTCDate();
  return day >= 1 && day <= last;
}
